import { useState } from 'react'

import {
  Icon,
  Modal,
  ModalProvider,
  PrimaryButton,
  SecondaryButton,
  TextField,
  typography,
} from '@joor/design-system'
import { useFormik } from 'formik'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import * as Yup from 'yup'

import { OrderTermsAndConditionsPlatform } from '__generated__/atlas-types'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'
import {
  getDateFormat,
  isLiteRetailer as isLiteRetailerSelector,
} from 'store/currentUser/selectors'

import { getFormattedAgreedOnTimeDate } from '../../components/Overview/components/LegalWidget/LegalWidget.utils'
import { ReviewPageIds } from '../../review.ids'
import { useUpdateOrderInfo } from 'features/Orders/Review/review.mutations'
import { useOrderId } from 'features/Orders/orders.hooks'

export enum LegalModalTestIds {
  Modal = 'Modal',
  InstructionsText = 'InstructionsText',
  FormContainer = 'FormContainer',
}

const TermsWrapper = styled.div`
  max-height: 261px;
  overflow-y: auto;
  margin-bottom: 16px;
  ${typography.alpha.primaryActive.body1}
  line-height: 16px;
`
const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 8px;

  background: var(--color-neutral-000-full);
  ${typography.alpha.primaryActive.body3}
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 15px;
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .instructions {
    ${typography.alpha.primaryActive.body1}
    margin: 0px;
  }

  .terms-declaration {
    ${typography.alpha.primaryActive.caption}
    margin: 0px;
  }

  .form-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;

    column-gap: 20px;
    row-gap: 16px;

    .e-signature {
      color: #8c9ba8;
      font-family: Dancing Script;
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0.6px;
    }
  }
`

type TermsFormValues = {
  termsTitle: string
  termsName: string
}

const TermsFormValidationSchema = Yup.object().shape({
  termsTitle: Yup.string().required().min(1),
  termsName: Yup.string().required().min(1),
})

export type LegalModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  terms: JSX.Element[]
  agreesWithTerms: boolean
  termsName?: string
  termsTitle?: string
  termsCompany?: string
  brandName?: string
  agreedPlatform?: OrderTermsAndConditionsPlatform
  agreedOn?: string
}

export const LegalModal = ({
  isOpen,
  setOpen,
  terms,
  agreesWithTerms,
  termsName = '',
  termsTitle = '',
  termsCompany = '',
  brandName = '',
  agreedPlatform,
  agreedOn,
}: LegalModalProps) => {
  const id = useOrderId()
  const Portal = usePortal()
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const userDateFormat = useSelector(getDateFormat)
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const { onUpdate, isUpdating } = useUpdateOrderInfo()

  const isLegacyTermsAndConditions =
    agreesWithTerms && !termsName && !termsTitle

  const isRevokable = !isLiteRetailer && agreesWithTerms
  const hasBeenRevoked = !agreesWithTerms && !!termsName && !!termsTitle
  const initialTermsName = hasBeenRevoked ? '' : termsName
  const initialTermsTitle = hasBeenRevoked ? '' : termsTitle
  const [eSignature, setESignature] = useState(initialTermsName)

  const formik = useFormik<TermsFormValues>({
    initialValues: {
      termsTitle: initialTermsTitle,
      termsName: initialTermsName,
    },
    validationSchema: TermsFormValidationSchema,
    validateOnChange: true,
    isInitialValid: !!initialTermsName && !!initialTermsTitle,
    onSubmit: async () => handleAgreeWithTerms(),
  })

  const handleAgreeWithTerms = async () => {
    try {
      const result = await onUpdate({
        orderIds: [id],
        orderDetails: {
          agreesWithTerms: true,
          termsAndConditions: {
            name: formik.values.termsName,
            title: formik.values.termsTitle,
            platform: OrderTermsAndConditionsPlatform.WEB,
          },
        },
      })
      if (result?.messages?.error) {
        renderErrorToast({
          title: 'Unable to accept T&C',
          description: 'An unexpected server error has occurred.',
          action: {
            label: 'Try again',
            handler: () => setOpen(true),
          },
        })
      } else {
        renderSuccessToast({
          title: 'T&C Accepted',
          description: 'Terms & Conditions have been successfully accepted.',
        })
      }
    } finally {
      setOpen(false)
    }
  }

  const handleRevokeTerms = async () => {
    try {
      const result = await onUpdate({
        orderIds: [id],
        orderDetails: { agreesWithTerms: false },
      })
      if (result?.messages?.error) {
        renderErrorToast({
          title: 'Unable to revoke T&C',
          description: 'An unexpected server error has occurred.',
          action: {
            label: 'Try again',
            handler: () => setOpen(true),
          },
        })
      } else {
        renderSuccessToast({
          title: 'T&C Revoked',
          description: 'Terms & Conditions have been successfully revoked.',
        })
      }
    } finally {
      setOpen(false)
    }
  }

  const disableTextFields = !isLiteRetailer || agreesWithTerms
  const requireTextFields = !agreesWithTerms && isLiteRetailer

  const platformText =
    agreedPlatform && agreedPlatform !== OrderTermsAndConditionsPlatform.UNKNOWN
      ? ` from ${agreedPlatform.toLowerCase()}`
      : ''
  const dateText = agreedOn
    ? ` on ${getFormattedAgreedOnTimeDate(agreedOn, userDateFormat)}`
    : ''
  const agreedInfoText = `Agreed to ${platformText} ${dateText}`

  const handleOnClose = () => {
    if (isLiteRetailer && !agreesWithTerms) {
      formik.resetForm()
      setESignature('')
    }
    setOpen(false)
  }

  const hideForm =
    isLegacyTermsAndConditions || (!isLiteRetailer && !isRevokable)

  return (
    <Portal>
      <ModalProvider>
        <Modal
          title={{ text: 'Terms & Conditions' }}
          isOpen={isOpen}
          onClose={(): void => handleOnClose()}
          testId={LegalModalTestIds.Modal}
          size="extra-large"
        >
          <Modal.Content>
            <TermsWrapper>{terms}</TermsWrapper>
            {!hideForm && (
              <FormWrapper data-testid={LegalModalTestIds.FormContainer}>
                {isLiteRetailer && (
                  <p
                    data-testid={LegalModalTestIds.InstructionsText}
                    className="instructions"
                  >
                    To agree to terms and conditions type in your name below.
                    Once agreed to, any changes will require you to reach out to{' '}
                    {brandName} directly.
                  </p>
                )}
                <form
                  className="form-container"
                  id={'termsForm'}
                  onSubmit={formik.handleSubmit}
                >
                  <TextField
                    label="Name"
                    id="termsName"
                    isRequired={requireTextFields}
                    placeholder="i.e. Jane Smith"
                    defaultValue={formik.values.termsName}
                    onChange={(e) => {
                      formik.handleChange(e)
                      setESignature(e.target.value)
                    }}
                    disabled={disableTextFields}
                  />
                  <TextField
                    label="eSignature"
                    value={eSignature}
                    className="e-signature"
                    disabled
                  />
                  <TextField
                    label="Title"
                    id="termsTitle"
                    isRequired={requireTextFields}
                    placeholder="i.e. Wholesale Buyer"
                    defaultValue={formik.values.termsTitle}
                    onChange={formik.handleChange}
                    disabled={disableTextFields}
                  />
                  <TextField
                    label="Company"
                    defaultValue={termsCompany}
                    disabled
                  />
                </form>
                <p className="terms-declaration">
                  These terms and conditions are intended to represent a legally
                  binding contract solely between the retailer and the brand.
                  JOOR is not a party to any aspect of the contracts for sale
                  between retailers and brands, including these terms and
                  conditions. JOOR makes no representations and warranties about
                  the subject matter of these terms and conditions, or their
                  enforceability.
                </p>
              </FormWrapper>
            )}
          </Modal.Content>
          <Modal.Actions>
            <ActionsWrapper>
              <CheckboxWrapper>
                <Icon
                  iconSize="small"
                  type={agreesWithTerms ? 'semantic' : 'default'}
                  iconName={agreesWithTerms ? 'success' : 'check-circle-on'}
                  variant={agreesWithTerms ? 'DEFAULT' : 'INACTIVE'}
                />
                <p>{agreesWithTerms ? agreedInfoText : 'Not agreed to'}</p>
              </CheckboxWrapper>
              {isLiteRetailer && !agreesWithTerms ? (
                <ButtonsWrapper>
                  <SecondaryButton
                    onClick={() => {
                      handleOnClose()
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type="submit"
                    form="termsForm"
                    loading={isUpdating}
                    disabled={!formik.isValid}
                  >
                    Agree
                  </PrimaryButton>
                </ButtonsWrapper>
              ) : (
                <ButtonsWrapper>
                  {isRevokable && (
                    <SecondaryButton
                      onClick={handleRevokeTerms}
                      loading={isUpdating}
                      disabled={!agreesWithTerms}
                      id={ReviewPageIds.RevokeTermButtonId}
                    >
                      Revoke
                    </SecondaryButton>
                  )}
                  <PrimaryButton onClick={() => setOpen(false)}>
                    Close
                  </PrimaryButton>
                </ButtonsWrapper>
              )}
            </ActionsWrapper>
          </Modal.Actions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}
